import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { CheckCircle, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { PAYMENT_METHODS } from './constants';

const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

export const PaymentDialog = ({ open, onOpenChange, selectedRegistration, paymentForm, setPaymentForm, onConfirm }) => {
  // Account-credit live balance for the parent on the picked registration.
  // null = not loaded yet, number = loaded (incl. 0).
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);

  // Fetch the parent's account credit balance when the dialog opens
  // OR when the admin switches the method to account_credit. We refetch
  // every time so a recent refund / consume shows the latest balance.
  useEffect(() => {
    if (!open) {
      setCreditBalance(null);
      return;
    }
    const parentId = selectedRegistration?.parent_id;
    const wantCredit = paymentForm.payment_method === 'account_credit';
    if (!parentId || !wantCredit) return;
    let cancelled = false;
    (async () => {
      setCreditLoading(true);
      try {
        const { data } = await api.get(`/parents/${parentId}/billing-info`);
        if (cancelled) return;
        setCreditBalance(
          Number(data?.customer_credit_balance || 0)
          + Number(data?.credit_balance || 0),
        );
      } catch {
        if (!cancelled) setCreditBalance(0);
      } finally {
        if (!cancelled) setCreditLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, paymentForm.payment_method, selectedRegistration?.parent_id]);

  const fee = Number(paymentForm.amount || 0);
  const isCreditMethod = paymentForm.payment_method === 'account_credit';
  const liveBalance = Number(creditBalance ?? 0);
  const willApply = Math.min(liveBalance, fee);
  const remainingDue = Math.max(0, +(fee - willApply).toFixed(2));
  const noCredit = isCreditMethod && creditBalance !== null && liveBalance <= 0;
  const isPartial = isCreditMethod && willApply > 0 && remainingDue > 0;

  // PR 19y5 — Overpayment detection for offline methods. Reference fee
  // is the amount that WAS owed before admin overrode: prefer
  // registration.balance_due (partial) → registration.amount → the
  // amount the dialog initialized to.
  const feeReference = Number(
    selectedRegistration?.balance_due
    ?? selectedRegistration?.amount
    ?? paymentForm?._initialAmount
    ?? 0
  );
  const isOfflineMethod = paymentForm.payment_method
    && !['card', 'stripe', 'account_credit'].includes(paymentForm.payment_method);
  const overage = isOfflineMethod && feeReference > 0 && fee > feeReference + 0.005
    ? +(fee - feeReference).toFixed(2)
    : 0;
  const overageAction = paymentForm.overage_action || 'credit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>Mark payment for {selectedRegistration?.player_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Payment Method</Label>
            <Select value={paymentForm.payment_method} onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(method => (
                  <SelectItem key={method.value} value={method.value} data-testid={`payment-method-${method.value}`}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Account credit breakdown — live balance, will-apply, remaining-due. */}
          {isCreditMethod && (
            <div
              className="rounded-md border bg-purple-50/40 border-purple-200 p-3 text-sm space-y-1.5"
              data-testid="payment-credit-breakdown"
            >
              <div className="flex items-center gap-1.5 font-medium text-purple-900">
                <Sparkles className="h-4 w-4" />
                Account Credit
              </div>
              {creditLoading ? (
                <div className="text-xs text-purple-700 flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading balance…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span className="text-slate-600">Available balance</span>
                    <span className="font-mono font-medium text-right" data-testid="credit-available">
                      {fmt(liveBalance)}
                    </span>
                    <span className="text-slate-600">Fee being recorded</span>
                    <span className="font-mono text-right" data-testid="credit-fee">{fmt(fee)}</span>
                    <span className="text-slate-600 font-medium">Will apply</span>
                    <span
                      className="font-mono font-semibold text-right text-purple-900"
                      data-testid="credit-will-apply"
                    >
                      {fmt(willApply)}
                    </span>
                    <span className={`font-medium ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      Remaining due
                    </span>
                    <span
                      className={`font-mono font-semibold text-right ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}
                      data-testid="credit-remaining"
                    >
                      {fmt(remainingDue)}
                    </span>
                  </div>
                  {noCredit && (
                    <div className="flex items-start gap-1.5 text-xs text-rose-700 mt-1">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        Parent has no account credit available. Pick another method, or add credit to their account from their profile first.
                      </span>
                    </div>
                  )}
                  {isPartial && !noCredit && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-800 mt-1" data-testid="payment-partial-warning">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        Credit will NOT cover the full fee. <strong>{fmt(willApply)}</strong> will be drained from credit
                        and the registration will be marked <strong>Partially Paid</strong> with <strong>{fmt(remainingDue)}</strong> still due.
                        The remainder must be collected via another method later.
                      </span>
                    </div>
                  )}
                  {!isPartial && !noCredit && (
                    <div className="text-xs text-emerald-700 mt-1" data-testid="payment-credit-full">
                      Credit covers the full fee — registration will be marked <strong>Paid</strong>.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <Label>Amount ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
              data-testid="payment-amount-input"
            />
            {isCreditMethod && (
              <p className="text-[11px] text-slate-500 mt-1">
                For account credit, this is the FEE you are billing — we'll drain up to that much from the parent's credit pool.
              </p>
            )}
          </div>
          <div>
            <Label>Notes (Optional)</Label>
            <Input
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              placeholder="e.g., Check #1234"
              data-testid="payment-notes-input"
            />
          </div>

          {overage > 0 && (
            <div
              className="rounded-md border bg-amber-50/60 border-amber-300 p-3 text-sm space-y-2"
              data-testid="tournament-payment-overage-alert"
            >
              <div className="flex items-start gap-1.5 text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Overpayment detected</div>
                  <div className="text-xs">
                    You entered <strong>{fmt(fee)}</strong> but the registration fee is
                    only <strong>{fmt(feeReference)}</strong> &mdash; that&apos;s{' '}
                    <strong>{fmt(overage)}</strong> extra.
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 pl-6">
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name="tourn_overage_action"
                    value="credit"
                    checked={overageAction === 'credit'}
                    onChange={() => setPaymentForm({ ...paymentForm, overage_action: 'credit' })}
                    className="mt-0.5"
                    data-testid="tournament-payment-overage-credit"
                  />
                  <span>
                    <strong>Deposit {fmt(overage)} to parent&apos;s account credit</strong>
                    <span className="block text-slate-600">
                      Registration paid at {fmt(feeReference)}; overage recorded as a
                      credit ledger entry for future use.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name="tourn_overage_action"
                    value="ignore"
                    checked={overageAction === 'ignore'}
                    onChange={() => setPaymentForm({ ...paymentForm, overage_action: 'ignore' })}
                    className="mt-0.5"
                    data-testid="tournament-payment-overage-ignore"
                  />
                  <span>
                    <strong>Only record {fmt(feeReference)} (registration fee)</strong>
                    <span className="block text-slate-600">
                      {fmt(overage)} not recorded anywhere — you handed the extra back
                      to the parent physically.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onConfirm}
            data-testid="confirm-payment-btn"
            disabled={noCredit || (isCreditMethod && creditLoading)}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            {isCreditMethod
              ? (isPartial ? `Apply ${fmt(willApply)} Credit (Partial)` : `Apply ${fmt(willApply)} Credit`)
              : overage > 0 && overageAction === 'credit'
                ? `Record ${fmt(feeReference)} + ${fmt(overage)} credit`
                : overage > 0 && overageAction === 'ignore'
                  ? `Record ${fmt(feeReference)}`
                  : 'Confirm Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
